import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { usePageBar } from "../contexts/PageBarContext";
import { routeAirportIds } from "../utils/flightRoute";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  doc,
  updateDoc,
  deleteDoc,
  deleteField,
  arrayUnion,
  arrayRemove,
  writeBatch,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import type { DocumentData } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useInteractions } from "../hooks/useInteractions";
import { useEntityMap } from "../hooks/useEntityMap";
import { QuickStepRow } from "../components/home/QuickStepRow";
import "../components/home/LiveNowCard.css";
import { upNextActivities } from "../utils/upNext";
import { useEntityHierarchy } from "../hooks/useEntityHierarchy";
import { useHotelStays, StayPositionsContext } from "../hooks/useHotelStays";
import { StaysSection } from "../components/stays/StayCard";
import { useSegments, createSegment } from "../hooks/useSegments";
import { TripForm } from "../components/trips/TripForm";
import { LogActivityPicker } from "../components/interactions/LogActivityPicker";
import { LogActivitySheet } from "../components/interactions/LogActivitySheet";
import { SegmentForm, type SegmentFormResult } from "../components/segments/SegmentForm";
import { AddDropdown } from "../components/trips/AddDropdown";
import { PeopleEditor, PeopleDisplay } from "../components/interactions/PeopleEditor";
import { TRIP_ROLE_TYPES } from "../config/peopleRoleTypes";
import { ActivityList } from "../components/itinerary/ActivityList";
import { SegmentBlock } from "../components/itinerary/SegmentBlock";
import { InteractionsAnalytics } from "../components/itinerary/InteractionsAnalytics";
import { InteractionsFilter } from "../components/itinerary/InteractionsFilter";
import {
  filterInteractions,
  EMPTY_FILTER,
  type InteractionFilter,
} from "../components/itinerary/interactionFilter";
import { LocationSharingControl } from "../components/trips/LocationSharingControl";
import { TripStory, TripViewSwitch } from "../components/trips/TripStory";
import { TripRecap } from "../components/trips/TripRecap";
import { buildTripRecap } from "../utils/tripRecap";
import { useClickOutside } from "../hooks/useClickOutside";
import { fireWrite } from "../utils/offlineWrites";
import { deepEqual, travelPartyEdit } from "../utils/editDiff";
import { onSnapshotResilient } from "../utils/resilientSnapshot";
import { NotesDisplay } from "../components/common/NotesDisplay";
import {
  segmentDayLookup,
  segmentZoneLookup,
  isSegmentCurrent,
  segmentDayCount,
  findCurrentSegmentId,
  lastLoggedSegmentId,
  activeSegmentChainIds,
  orderSegmentsNewestFirst,
  segmentLogSeeds,
  segmentSubtreeActivityMs,
  activityEnd,
  isActivityActive,
  tripStatusLabel,
} from "../utils/tripDisplay";
import { dayKeyOf, daysBetweenKeys, formatDayKey, todayKey } from "../utils/calendarDay";
import { findDefaultSegmentId } from "../utils/activityLogging";
import type { Trip, TravelPartyMember, Person } from "../types";
import { getInteractionEntityType } from "../types";

type AddMode = null | "segment" | "activity";

export function TripDetail() {
  const { tripId } = useParams<{ tripId: string }>();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [trip, setTrip] = useState<Trip | null>(null);
  const [loading, setLoading] = useState(true);
  const { interactions, loading: interactionsLoading } = useInteractions(tripId);
  const { segments, loading: segmentsLoading } = useSegments(tripId);

  const topLevelSegments = segments.filter((s) => !s.parentSegmentId);
  // Most recent activity time per segment subtree — drives newest-first ordering
  // so the segment you last logged in rises to the top.
  const segmentActivityMs = useMemo(
    () => segmentSubtreeActivityMs(segments, interactions),
    [segments, interactions],
  );

  const entityIds = interactions.map((i) => i.entityId).filter((id): id is string => id != null);
  // Only what's still an open question — activities settled as "not a place"
  // have been dealt with and shouldn't keep the nudge alive.
  const unlinkedCount = interactions.filter((i) => !i.entityId && !i.noPlace).length;
  const segmentEntityIds = segments.map((s) => s.entityId).filter((id): id is string => id != null);
  const allEntityIds = [...new Set([...entityIds, ...segmentEntityIds, ...routeAirportIds(interactions)])];
  const { entityMap, entityDataMap, loading: entitiesLoading } = useEntityMap(allEntityIds);
  // Ancestor chains for the logged entities, so a Location filter (a park /
  // resort) matches everything beneath it.
  const {
    chains: entityChains,
    nodes: entityNodes,
    loading: hierarchyLoading,
  } = useEntityHierarchy(entityIds);

  // Hotel stays, worked out from the hotel visits (see utils/hotelStays).
  const onDayLookup = useMemo(() => segmentDayLookup(segments), [segments]);
  // What's open right now, and what's set up waiting for its first step: at
  // the top, so the next tap isn't somewhere down the itinerary.
  const typeOfActivity = (i: (typeof interactions)[number]) =>
    getInteractionEntityType(
      i.entityId ? { type: entityDataMap[i.entityId]?.type ?? undefined } : null,
      i.customResponses,
    );
  const nowRows = useMemo(() => {
    const open = interactions
      .filter((i) => isActivityActive(i.events, typeOfActivity(i), i.customResponses))
      .sort((a, b) => (activityEnd(b, onDayLookup)?.getTime() ?? 0) - (activityEnd(a, onDayLookup)?.getTime() ?? 0));
    return [...upNextActivities(interactions), ...open];
    // typeOfActivity reads entityDataMap, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactions, entityDataMap, onDayLookup]);
  const { stays, positions: stayPositions } = useHotelStays(interactions, entityDataMap, onDayLookup);

  // A finished trip opens on its story; the itinerary is a tap away (?view=itinerary).
  const [searchParams, setSearchParams] = useSearchParams();
  const [recapOpen, setRecapOpen] = useState(false);
  const tripZone = trip?.timezone ?? undefined;
  const recap = useMemo(
    () =>
      buildTripRecap(
        interactions,
        (id) => (id ? entityDataMap[id] : null),
        onDayLookup,
        segmentZoneLookup(segments, tripZone),
      ),
    [interactions, entityDataMap, onDayLookup, segments, tripZone],
  );

  // Trip-level filter — drives both the itinerary and the analytics.
  const [filter, setFilter] = useState<InteractionFilter>(EMPTY_FILTER);
  // Itinerary order — remembered across trips/sessions (on-trip: newest first;
  // reviewing: oldest → newest).
  const [sortDir, setSortDir] = useState<"asc" | "desc">(() => {
    try {
      return localStorage.getItem("triptracker:itinerarySortDir") === "desc" ? "desc" : "asc";
    } catch {
      return "asc";
    }
  });
  const toggleSortDir = () => {
    setSortDir((prev) => {
      const next = prev === "desc" ? "asc" : "desc";
      try {
        localStorage.setItem("triptracker:itinerarySortDir", next);
      } catch {
        /* ignore storage failures (private mode) */
      }
      return next;
    });
  };
  const filteredInteractions = useMemo(
    () => filterInteractions(interactions, filter, entityDataMap, segments, entityChains, trip?.timezone),
    [interactions, filter, entityDataMap, segments, entityChains, trip?.timezone],
  );
  const unsegmentedInteractions = filteredInteractions.filter((i) => !i.segmentId);

  const [editing, setEditing] = useState(false);
  // The trip as the edit form opened on it: a save writes only the fields
  // changed from this, so a rename or a notes edit made on another phone
  // meanwhile isn't reverted by one that didn't touch them.
  const [openedTrip, setOpenedTrip] = useState<{ name: string; start: string | null; end: string | null; notes: string | null } | null>(null);
  const [addMode, setAddMode] = useState<AddMode>(null);
  const [activityDefaultSegmentId, setActivityDefaultSegmentId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useClickOutside<HTMLDivElement>(menuOpen, () => setMenuOpen(false));
  const [editingPeople, setEditingPeople] = useState(false);
  usePageBar({
    title: trip?.name ?? null,
    parent: { to: "/trips", label: "My Trips" },
    cancel: editing ? { onClick: () => setEditing(false) } : null,
  });
  const [editPeople, setEditPeople] = useState<Person[]>([]);
  // The crew list as the editor opened with it — what a save diffs against.
  const [openedPeople, setOpenedPeople] = useState<Person[]>([]);
  const [expandedSegments, setExpandedSegments] = useState<Set<string>>(new Set());
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const didAutoExpand = useRef(false);

  // Trip-level log defaults to the current (active) segment, else the last
  // activity's segment.
  const defaultLoggingSegmentId = useMemo(
    () =>
      findCurrentSegmentId(segments, lastLoggedSegmentId(interactions)) ??
      findDefaultSegmentId(segments, interactions),
    [segments, interactions]
  );
  // Only the segment you're in (and its parents) wear the "NOW" tag, so a
  // handoff day doesn't show two.
  const activeSegmentIds = useMemo(
    () => activeSegmentChainIds(segments, lastLoggedSegmentId(interactions)),
    [segments, interactions],
  );
  // Seed "At" scoped to the segment being logged under (the clicked segment,
  // else the active one): its recent activities + the segment's entity chain.
  const seedEntityIds = useMemo(
    () => segmentLogSeeds(segments, interactions, activityDefaultSegmentId ?? defaultLoggingSegmentId),
    [segments, interactions, activityDefaultSegmentId, defaultLoggingSegmentId],
  );

  // Auto-expand current segments (and their ancestors) once segments load
  useEffect(() => {
    if (didAutoExpand.current || segments.length === 0) return;
    const byId = new Map(segments.map((s) => [s.id, s]));
    const toExpand = new Set<string>();
    for (const s of segments) {
      if (!isSegmentCurrent(s.startDate, s.endDate)) continue;
      toExpand.add(s.id);
      let parentId = s.parentSegmentId;
      while (parentId) {
        toExpand.add(parentId);
        parentId = byId.get(parentId)?.parentSegmentId ?? null;
      }
    }
    if (toExpand.size > 0) setExpandedSegments(toExpand);
    didAutoExpand.current = true;
  }, [segments]);

  useEffect(() => {
    if (!tripId) return;
    // Resilient: `TripCreate` navigates here as soon as the create is queued
    // locally, so this listener can open before the trip exists on the server —
    // where the rules deny it. See `resilientSnapshot.ts`.
    const unsub = onSnapshotResilient(
      doc(db, "trips", tripId),
      (snapshot) => {
        if (snapshot.exists()) {
          setTrip(snapshot.data() as Trip);
        }
        setLoading(false);
      },
      {
        label: `trip ${tripId}`,
        onError: ({ error, hadPendingWrites }) => {
          setLoading(false);
          // A denial that isn't about our own unsynced write means this trip
          // isn't ours to see — we were removed from the travel party, or the
          // link came from someone else. Firestore served it from cache before
          // the rules ran, so drop it rather than keep a trip on screen that
          // the server just refused.
          if (error.code === "permission-denied" && !hadPendingWrites) setTrip(null);
        },
      },
    );
    return unsub;
  }, [tripId]);

  if (loading) return <div className="loading">Loading trip...</div>;
  if (!trip || !tripId) return <p>Trip not found. <Link to="/trips">Back to trips</Link></p>;

  const openLogActivity = (segmentId: string | null) => {
    setActivityDefaultSegmentId(segmentId);
    setAddMode("activity");
  };
  const closeLogActivity = () => {
    setAddMode(null);
    setActivityDefaultSegmentId(null);
  };

  const toggleSegment = (segmentId: string) => {
    setExpandedSegments((prev) => {
      const next = new Set(prev);
      if (next.has(segmentId)) next.delete(segmentId);
      else next.add(segmentId);
      return next;
    });
  };

  const handleEditSubmit = async (data: {
    name: string;
    startDate: Date;
    endDate: Date;
    notes: string;
    travelParty?: Record<string, TravelPartyMember>;
    openedTravelParty?: Record<string, TravelPartyMember>;
  }) => {
    const updates: DocumentData = { updatedAt: serverTimestamp() };
    const opened = openedTrip;
    if (!opened || data.name !== opened.name) updates.name = data.name;
    if (!opened || dayKeyOf(data.startDate) !== opened.start) updates.startDate = Timestamp.fromDate(data.startDate);
    if (!opened || dayKeyOf(data.endDate) !== opened.end) updates.endDate = Timestamp.fromDate(data.endDate);
    if (!opened || (data.notes || null) !== opened.notes) updates.notes = data.notes || null;
    // The party is shared with everyone else on the trip — an accepted invite
    // fills in a member's userId, and each member flips their own location
    // sharing — so it's never written back whole from the form's copy. Only the
    // members this edit added or removed are touched, each at its own path, and
    // memberUserIds moves by arrayUnion/arrayRemove rather than being rebuilt.
    const party = data.travelParty
      ? travelPartyEdit(data.openedTravelParty ?? {}, data.travelParty, trip.travelParty ?? {})
      : null;
    if (party) {
      for (const [key, member] of Object.entries(party.set)) updates[`travelParty.${key}`] = member;
      for (const key of party.remove) updates[`travelParty.${key}`] = deleteField();
      if (party.addUserIds.length > 0) updates.memberUserIds = arrayUnion(...party.addUserIds);
    }
    // A field takes one transform per update, so removals go in a second
    // update of the same batch (a batch, unlike a transaction, works offline).
    const ref = doc(db, "trips", tripId);
    const batch = writeBatch(db);
    batch.update(ref, updates);
    if (party && party.removeUserIds.length > 0) {
      batch.update(ref, { memberUserIds: arrayRemove(...party.removeUserIds) });
    }
    fireWrite(batch.commit(), "save trip");
    setEditing(false);
  };

  const handleStartEditPeople = () => {
    setEditPeople(trip.people || []);
    setOpenedPeople(trip.people || []);
    setEditingPeople(true);
  };

  const handleSavePeople = async () => {
    // The list is written whole, so only when this edit actually changed it —
    // saving an untouched form mustn't revert someone else's edit made since.
    const next = editPeople.filter((p) => p.name.trim());
    if (!deepEqual(next, openedPeople.filter((p) => p.name.trim()))) {
      fireWrite(updateDoc(doc(db, "trips", tripId), {
        people: next,
        updatedAt: serverTimestamp(),
      }), "save trip people");
    }
    setEditingPeople(false);
  };

  const handleDelete = async () => {
    if (!confirm("Delete this trip and all its interactions? This cannot be undone.")) return;
    setDeleting(true);
    fireWrite(deleteDoc(doc(db, "trips", tripId)), "delete trip");
    navigate("/trips");
  };

  const handleAddSegment = async (data: SegmentFormResult) => {
    const segmentId = await createSegment(tripId, data);
    setAddMode(null);
    navigate(`/trips/${tripId}/segments/${segmentId}`);
  };

  const travelPartyEntries = Object.entries(trip.travelParty || {});
  const isOrganizer = trip.createdBy === user?.uid;
  // Any linked trip member can edit trip details and segments (Firestore rules
  // already allow this); only the organizer/owner can delete the trip.
  const isMember = !!user && (trip.memberUserIds?.includes(user.uid) ?? false);

  // A trip's first and last days are calendar days, the same wherever read.
  const formatDateRange = () => {
    const start = dayKeyOf(trip.startDate);
    const end = dayKeyOf(trip.endDate);
    if (!start || !end) return "";
    const opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" };
    return `${formatDayKey(start, opts)} — ${formatDayKey(end, opts)}`;
  };

  const daysUntil = () => {
    const start = dayKeyOf(trip.startDate);
    if (!start) return null;
    const today = todayKey();
    const diff = daysBetweenKeys(today, start);
    if (diff > 0) return `${diff} days away`;
    if (diff === 0) return "Today!";
    const end = dayKeyOf(trip.endDate);
    if (end && today <= end) return "Happening now";
    return "Past trip";
  };

  const isPast = tripStatusLabel(trip.startDate, trip.endDate).tone === "past";
  const storyLoading = interactionsLoading || entitiesLoading;
  const wantsItinerary = searchParams.get("view") === "itinerary";
  const hasStory = isPast && (storyLoading || recap.days.length > 0);
  const setView = (view: "story" | "itinerary") =>
    setSearchParams(view === "itinerary" ? { view } : {}, { replace: true });

  if (hasStory && !wantsItinerary && !editing) {
    // First names read better on the cover ("With Amy, Cameron and Travis").
    const party = travelPartyEntries.map(([, m]) => m.displayName.trim().split(/\s+/)[0] ?? "").filter(Boolean);
    const dates = formatDateRange();
    return (
      <div className="page">
        {storyLoading ? (
          <div className="loading">Loading trip...</div>
        ) : (
          <>
            <TripStory
              tripId={tripId}
              name={trip.name}
              dates={dates}
              party={party}
              startKey={dayKeyOf(trip.startDate)}
              recap={recap}
              onPlayRecap={() => setRecapOpen(true)}
              onShowItinerary={() => setView("itinerary")}
            />
            <p className="text-muted">
              <Link to={`/trips/${tripId}/journey`}>See the journey map</Link>
            </p>
            {recapOpen && (
              <TripRecap recap={recap} trip={{ name: trip.name, dates, party }} onClose={() => setRecapOpen(false)} />
            )}
          </>
        )}
      </div>
    );
  }

  if (editing) {
    return (
      <div className="page">
        <h1>Edit Trip</h1>
        <TripForm
          initial={trip}
          onSubmit={handleEditSubmit}
          onCancel={() => setEditing(false)}
          submitLabel="Save Changes"
        />
      </div>
    );
  }

  const dropdownItems = [
    { label: "Add Segment", onClick: () => setAddMode("segment") },
    { label: "Log Activity", onClick: () => openLogActivity(null) },
  ];

  return (
    <div className="page">
      <div className="nowpass trip-pass">
        <div className="trip-pass-actions">
          <LocationSharingControl
            tripId={tripId}
            trip={trip}
            accountDefault={userProfile?.defaultLocationSharing === true}
          />
          <div className="trip-pass-menu" ref={menuRef}>
            <button
              type="button"
              className="dp-menu-trigger"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Trip actions"
              title="Trip actions"
            >
              &#8942;
            </button>
            {menuOpen && (
              <div className="detail-menu-dropdown">
                <Link to={`/trips/${tripId}/journey`} onClick={() => setMenuOpen(false)}>
                  🗺️ Journey
                </Link>
                {isMember && (
                  <button
                    onClick={() => {
                      setOpenedTrip({
                        name: trip.name,
                        start: dayKeyOf(trip.startDate),
                        end: dayKeyOf(trip.endDate),
                        notes: trip.notes || null,
                      });
                      setEditing(true);
                      setMenuOpen(false);
                    }}
                  >
                    Edit
                  </button>
                )}
                {isOrganizer && (
                  <button
                    className="detail-menu-danger"
                    onClick={() => { setMenuOpen(false); handleDelete(); }}
                    disabled={deleting}
                  >
                    {deleting ? "Deleting..." : "Delete"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="np-body">
          {/* For a finished trip, the Story switch lives in the card, so the
              page opens on the trip rather than on chrome. */}
          <div className="np-eyebrow trip-pass-top">
            {hasStory ? (
              <TripViewSwitch view="itinerary" onChange={setView} />
            ) : (
              <span>{daysUntil() || "Trip"}</span>
            )}
          </div>
          <h2>{trip.name}</h2>
          <div className="np-sub">{formatDateRange()}</div>
          {travelPartyEntries.length > 0 && (
            <div className="np-party">
              {travelPartyEntries.map(([key, member]) => (
                <span key={key} className="np-av" title={member.displayName}>
                  {member.avatarUrl ? (
                    <img src={member.avatarUrl} alt={member.displayName} />
                  ) : (
                    member.displayName.charAt(0).toUpperCase()
                  )}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="np-perf">
          <div className="p">
            <div className="k">Segments</div>
            <div className="v">{topLevelSegments.length}</div>
          </div>
          <div className="p">
            <div className="k">Logged</div>
            <div className="v">{interactions.length}</div>
          </div>
          <div className="p">
            <div className="k">Days</div>
            <div className="v">{segmentDayCount(trip.startDate, trip.endDate)}</div>
          </div>
        </div>
      </div>

      {nowRows.length > 0 && (
        <section className="quick-steps trip-now" aria-labelledby="trip-now-title">
          <h2 id="trip-now-title">Happening now</h2>
          {nowRows.map((i) => (
            <QuickStepRow
              key={i.id}
              activity={i}
              entity={i.entityId ? entityDataMap[i.entityId] ?? null : null}
              type={typeOfActivity(i)}
              tripId={tripId}
              zone={trip.timezone ?? undefined}
            />
          ))}
        </section>
      )}

      {trip.notes && <NotesDisplay value={trip.notes} className="detail-notes" />}

      {addMode === "segment" && (
        <div className="add-form-wrapper">
          <SegmentForm
            onSubmit={handleAddSegment}
            onCancel={() => setAddMode(null)}
            segments={segments}
            travelParty={trip.travelParty || {}}
          />
        </div>
      )}

      <LogActivitySheet open={addMode === "activity"} onClose={closeLogActivity}>
        <LogActivityPicker
          tripId={tripId}
          segments={segments}
          defaultSegmentId={activityDefaultSegmentId ?? defaultLoggingSegmentId}
          seedEntityIds={seedEntityIds}
          interactions={interactions}
        />
      </LogActivitySheet>

      {((trip.people || []).length > 0 || editingPeople) && (
        <section>
          {editingPeople ? (
            <>
              <PeopleEditor people={editPeople} onChange={setEditPeople} roleTypes={TRIP_ROLE_TYPES} />
              <div className="form-actions">
                <button className="btn btn-small" onClick={handleSavePeople}>Save</button>
                <button className="btn btn-secondary btn-small" onClick={() => setEditingPeople(false)}>Cancel</button>
              </div>
            </>
          ) : (
            <>
              <div className="section-header">
                <h2>People</h2>
                {isMember && (
                  <button className="btn btn-small btn-secondary" onClick={handleStartEditPeople}>Edit</button>
                )}
              </div>
              <PeopleDisplay people={trip.people} roleTypes={TRIP_ROLE_TYPES} />
            </>
          )}
        </section>
      )}

      {!interactionsLoading && !entitiesLoading && (
        <StaysSection stays={stays} entityDataMap={entityDataMap} tripId={tripId} zone={trip.timezone ?? undefined} />
      )}

      <StayPositionsContext.Provider value={stayPositions}>
      <section>
        <div className="section-header">
          <h2 className="itinerary-title">Itinerary</h2>
          <AddDropdown items={dropdownItems} />
        </div>

        {/* Activities logged by name only — they don't reach a place, and
            repeats of the same place don't reach each other. */}
        {unlinkedCount > 0 && (
          <Link to={`/entities/link?tripId=${tripId}`} className="trip-unlinked-note">
            {unlinkedCount === 1
              ? "1 activity has no place linked"
              : `${unlinkedCount} activities have no place linked`}{" "}
            — link them &rarr;
          </Link>
        )}

        {interactions.length > 0 && !entitiesLoading && (
          <InteractionsFilter
            tripZone={trip?.timezone}
            interactions={interactions}
            entityDataMap={entityDataMap}
            segments={segments}
            chains={entityChains}
            nodes={entityNodes}
            locationsLoading={hierarchyLoading}
            value={filter}
            onChange={setFilter}
            sortDir={sortDir}
            onToggleSort={toggleSortDir}
          />
        )}

        {segmentsLoading || interactionsLoading || entitiesLoading ? (
          <div className="loading">Loading...</div>
        ) : (
          <div className="itinerary">
            {(sortDir === "desc"
              ? orderSegmentsNewestFirst(
                  topLevelSegments,
                  segmentActivityMs,
                  (s) => s.endDate?.toMillis?.() ?? s.startDate?.toMillis?.() ?? 0,
                )
              : topLevelSegments
            ).map((segment) => (
              <SegmentBlock
                key={segment.id}
                segment={segment}
                allSegments={segments}
                interactions={filteredInteractions}
                entityMap={entityMap}
                entityDataMap={entityDataMap}
                tripId={tripId}
                expandedSegments={expandedSegments}
                toggleSegment={toggleSegment}
                onAddActivity={openLogActivity}
                sortDir={sortDir}
                activityMs={segmentActivityMs}
                activeSegmentIds={activeSegmentIds}
              />
            ))}

            {unsegmentedInteractions.length > 0 && (
              <ActivityList
                activities={unsegmentedInteractions}
                entityMap={entityMap}
                entityDataMap={entityDataMap}
                tripId={tripId}
                sortDir={sortDir}
                onDay={segmentDayLookup(segments)}
                onZone={segmentZoneLookup(segments, trip?.timezone ?? undefined)}
              />
            )}

            {topLevelSegments.length === 0 && unsegmentedInteractions.length === 0 && !entitiesLoading && (
              <div className="itinerary-empty">
                <p className="text-muted">No itinerary yet. Use the <strong>+</strong> button to add segments and activities.</p>
              </div>
            )}
          </div>
        )}
      </section>
      </StayPositionsContext.Provider>

      {interactions.length > 0 && !interactionsLoading && !entitiesLoading && (
        <section>
          <button
            type="button"
            className="analytics-disclosure"
            onClick={() => setAnalyticsOpen((o) => !o)}
            aria-expanded={analyticsOpen}
          >
            <h2>Analytics</h2>
            <span className={`analytics-chevron${analyticsOpen ? " open" : ""}`} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </span>
          </button>
          {/* Mounted only when expanded, so the stats aren't computed on every
              trip visit. */}
          {analyticsOpen ? (
            <InteractionsAnalytics
              interactions={filteredInteractions}
              entityDataMap={entityDataMap}
            />
          ) : (
            <p className="text-muted analytics-hint">
              {filteredInteractions.length}{" "}
              {filteredInteractions.length === 1 ? "activity" : "activities"} · tap to view wait
              times, tiers &amp; charts
            </p>
          )}
        </section>
      )}
    </div>
  );
}
